// Zustand store for recording state

import { create } from 'zustand'

export type RecordingStatus = 'idle' | 'initializing' | 'ready' | 'recording' | 'uploading' | 'complete' | 'error'

interface RecordingState {
  // Recording status
  status: RecordingStatus

  // Session management
  sessionToken: string | null

  // Recording progress
  chunksUploaded: number
  recordingDuration: number // seconds

  // Error handling
  error: string | null

  // Location
  includeLocation: boolean
  gpsLat: number | null
  gpsLng: number | null

  // Last completed video
  lastVideoId: string | null

  // Actions
  setStatus: (status: RecordingStatus) => void
  setSessionToken: (token: string | null) => void
  incrementChunks: () => void
  setRecordingDuration: (duration: number) => void
  setError: (error: string | null) => void
  setLocation: (lat: number, lng: number) => void
  toggleLocation: () => void
  setLastVideoId: (id: string | null) => void
  reset: () => void
}

const initialState = {
  status: 'idle' as RecordingStatus,
  sessionToken: null,
  chunksUploaded: 0,
  recordingDuration: 0,
  error: null,
  includeLocation: true,
  gpsLat: null,
  gpsLng: null,
  lastVideoId: null,
}

export const useRecordingStore = create<RecordingState>((set) => ({
  ...initialState,

  setStatus: (status) => set({ status }),

  setSessionToken: (token) => set({ sessionToken: token }),

  incrementChunks: () => set((state) => ({ chunksUploaded: state.chunksUploaded + 1 })),

  setRecordingDuration: (duration) => set({ recordingDuration: duration }),

  setError: (error) => set({ error, status: error ? 'error' : 'idle' }),

  setLocation: (lat, lng) => set({ gpsLat: lat, gpsLng: lng }),

  toggleLocation: () => set((state) => ({ includeLocation: !state.includeLocation })),

  setLastVideoId: (id) => set({ lastVideoId: id }),

  reset: () => set({
    ...initialState,
    // Preserve location preference
    includeLocation: useRecordingStore.getState().includeLocation,
  }),
}))
